import { getDynamicPageMeta } from '@/lib/dynamicPageMeta';

// Central SEO metadata for public routes (PL / EN).
// Titles ≤ 60 chars ending "| TerpeneAtlas" (except the homepage, set exactly per brief).
// Descriptions 140–160 chars.

export const BASE_URL = 'https://terpeneatlas.org';

export const PRIVATE_PATH_PREFIXES = ['/admin/', '/analytics', '/alert-manager', '/ustawienia', '/ulubione'];

export function isPrivatePath(pathname) {
  return PRIVATE_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
}

const SYNERGY = {
  pl: {
    title: 'Mapa synergii terpenów | TerpeneAtlas',
    description: 'Interaktywna mapa terpenów medycznej marihuany: zobacz, jak mircen, limonen czy kariofilen wzmacniają się nawzajem i jak powstaje efekt anturażu w odmianach.',
  },
  en: {
    title: 'Terpene Synergy Map | TerpeneAtlas',
    description: 'Interactive medical cannabis terpene map: see how myrcene, limonene or caryophyllene reinforce each other and how the entourage effect emerges in strains.',
  },
};

export const PAGE_META = {
  '/': {
    pl: {
      title: 'TerpeneAtlas – terpeny, odmiany i porównywarka suszu medycznej marihuany',
      description: 'Bezpłatna baza wiedzy o medycznej marihuanie: profile terpenowe odmian, opinie pacjentów, dostępność w aptekach i porównywarka suszu. Poradnik dla pacjentów i lekarzy.',
    },
    en: {
      title: 'TerpeneAtlas – terpenes, strains and medical cannabis flower comparison',
      description: 'Free medical cannabis knowledge base: strain terpene profiles, patient reviews, pharmacy availability and a flower comparison tool. A guide for patients and doctors.',
    },
  },
  '/terpeny': {
    pl: {
      title: 'Encyklopedia terpenów konopi | TerpeneAtlas',
      description: 'Encyklopedia terpenów medycznej marihuany: mircen, limonen, kariofilen i inne. Działanie, temperatury wrzenia, źródła naukowe i odmiany bogate w dany terpen.',
    },
    en: {
      title: 'Cannabis Terpene Encyclopedia | TerpeneAtlas',
      description: 'Medical cannabis terpene encyclopedia: myrcene, limonene, caryophyllene and more. Effects, boiling points, scientific evidence and strains rich in each terpene.',
    },
  },
  '/terpene-map': SYNERGY,
  '/terpene-synergy': SYNERGY,
  '/glossary': {
    pl: {
      title: 'Słowniczek pojęć medycznej marihuany | TerpeneAtlas',
      description: 'Słowniczek medycznej marihuany: terpeny, kannabinoidy, THC, CBD, receptory CB1 i CB2, efekt anturażu, waporyzacja — trudne pojęcia wyjaśnione prostym językiem.',
    },
    en: {
      title: 'Medical Cannabis Glossary | TerpeneAtlas',
      description: 'Medical cannabis glossary: terpenes, cannabinoids, THC, CBD, CB1 and CB2 receptors, entourage effect, vaporisation — complex terms explained in plain language.',
    },
  },
  '/poradnik-pacjenta': {
    pl: {
      title: 'Przewodnik Pacjenta | TerpeneAtlas',
      description: 'Poradnik pacjenta medycznej marihuany: jak czytać profil terpenowy, efekt anturażu, indica vs sativa, interakcje z lekami i bezpieczeństwo terapii konopnej.',
    },
    en: {
      title: 'Patient Guide | TerpeneAtlas',
      description: 'Medical cannabis patient guide: how to read a terpene profile, the entourage effect, indica vs sativa, drug interactions and the safety of cannabis therapy.',
    },
  },
  '/porownanie-terpenow': {
    pl: {
      title: 'Porównanie terpenów konopi | TerpeneAtlas',
      description: 'Porównaj terpeny medycznej marihuany obok siebie: działanie, aromat, temperatura wrzenia i degradacji. Sprawdź, które terpeny najlepiej pasują do Twoich potrzeb.',
    },
    en: {
      title: 'Compare Cannabis Terpenes | TerpeneAtlas',
      description: 'Compare medical cannabis terpenes side by side: effects, aroma, boiling and degradation temperatures. Find out which terpenes best match your therapeutic needs.',
    },
  },
  '/porownanie-odmian': {
    pl: {
      title: 'Porównywarka odmian medycznej marihuany | TerpeneAtlas',
      description: 'Porównaj do 4 odmian medycznej marihuany: profile terpenowe, THC, CBD, temperatury waporyzacji i ceny w aptekach. Zobacz różnice odmian na jednym ekranie.',
    },
    en: {
      title: 'Medical Cannabis Strain Comparison | TerpeneAtlas',
      description: 'Compare up to 4 medical cannabis strains: terpene profiles, THC, CBD, vaporisation temperatures and pharmacy prices. See the differences on a single screen.',
    },
  },
  '/dostepnosc-cen': {
    pl: {
      title: 'Dostępność i ceny suszu w aptekach | TerpeneAtlas',
      description: 'Aktualna dostępność i ceny suszu medycznej marihuany w polskich aptekach. Sprawdź, które odmiany są dostępne, i ustaw powiadomienie o pojawieniu się leku.',
    },
    en: {
      title: 'Cannabis Flower Availability & Prices | TerpeneAtlas',
      description: 'Current availability and prices of medical cannabis flower in Polish pharmacies. Check which strains are in stock and set an alert for when your medicine arrives.',
    },
  },
  '/o-nas': {
    pl: {
      title: 'O portalu – misja i autorka | TerpeneAtlas',
      description: 'TerpeneAtlas to niezależna, bezpłatna baza wiedzy o medycznej marihuanie tworzona przez lekarkę. Poznaj misję portalu, źródła danych i zasady rzetelności.',
    },
    en: {
      title: 'About – Mission and Author | TerpeneAtlas',
      description: 'TerpeneAtlas is an independent, free medical cannabis knowledge base created by a physician. Learn about the site\'s mission, data sources and editorial standards.',
    },
  },
  '/kontakt': {
    pl: {
      title: 'Kontakt | TerpeneAtlas',
      description: 'Skontaktuj się z redakcją TerpeneAtlas: pytania o terpeny, odmiany medycznej marihuany, błędy w danych lub współpracę. Odpowiadamy na każdą wiadomość.',
    },
    en: {
      title: 'Contact | TerpeneAtlas',
      description: 'Get in touch with the TerpeneAtlas team: questions about terpenes, medical cannabis strains, data corrections or collaboration. We reply to every message.',
    },
  },
  '/nowosci': {
    pl: {
      title: 'Aktualności i nowe odmiany | TerpeneAtlas',
      description: 'Aktualności TerpeneAtlas: nowe odmiany medycznej marihuany w polskich aptekach, zmiany w danych o terpenach i nowe funkcje portalu. Bądź na bieżąco.',
    },
    en: {
      title: 'News and New Strains | TerpeneAtlas',
      description: 'TerpeneAtlas news: new medical cannabis strains in Polish pharmacies, terpene data updates and new site features. Stay up to date with the latest changes.',
    },
  },
  '/szukaj': {
    pl: {
      title: 'AI doradca – wyszukiwarka odmian | TerpeneAtlas',
      description: 'Opisz swoje potrzeby, a AI doradca podpowie odmiany medycznej marihuany o pasującym profilu terpenowym. Wyszukiwanie po objawach, terpenach i działaniu.',
    },
    en: {
      title: 'AI Budtender – Strain Search | TerpeneAtlas',
      description: 'Describe your needs and the AI budtender suggests medical cannabis strains with a matching terpene profile. Search by symptoms, terpenes and desired effects.',
    },
  },
  '/umow-wizyte': {
    pl: {
      title: 'Umów wizytę u lekarza konopnego | TerpeneAtlas',
      description: 'Umów konsultację lekarską w sprawie terapii medyczną marihuaną. Kwalifikacja do leczenia, dobór odmiany i dawki, recepta — wizyta online lub stacjonarnie.',
    },
    en: {
      title: 'Book a Cannabis Doctor Consultation | TerpeneAtlas',
      description: 'Book a medical consultation about medical cannabis therapy. Treatment qualification, strain and dose selection, prescription — online or in-person appointment.',
    },
  },
  '/polityka-prywatnosci': {
    pl: {
      title: 'Polityka prywatności | TerpeneAtlas',
      description: 'Polityka prywatności TerpeneAtlas: jakie dane przetwarzamy, w jakim celu i jak je chronimy. Informacje o cookies, analityce i prawach użytkowników portalu.',
    },
    en: {
      title: 'Privacy Policy | TerpeneAtlas',
      description: 'TerpeneAtlas privacy policy: what data we process, for what purpose and how we protect it. Information on cookies, analytics and the rights of site users.',
    },
  },
};

export function getPageMeta(pathname, language) {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const entry = PAGE_META[clean];
  if (!entry) return getDynamicPageMeta(clean, language);
  return entry[language === 'en' ? 'en' : 'pl'];
}